/**
 * Sep 04, 2026 · IST-first date/time formatting helpers.
 *
 * Every audit-facing surface (Sales Orders, GRNs, Audit Logs, Approvals,
 * dashboards) must render timestamps in `DD-MM-YYYY HH:MM` in IST regardless
 * of the browser's local timezone. Backend now stamps every `created_at`,
 * `updated_at`, `authorized_at` with `Asia/Kolkata` offset (`+05:30`), so
 * these helpers just format the incoming ISO string through the same TZ.
 *
 * Never fall back to `toLocaleString()` without an explicit `timeZone` —
 * a browser in London or New York would otherwise silently reformat
 * factory-floor timestamps to their local wall-clock.
 */

const IST = "Asia/Kolkata";

// Two-digit padder — cheaper than Intl.NumberFormat when we only need
// per-field padding for a fixed date format.
const _pad = (n) => String(n).padStart(2, "0");

/**
 * Parse an ISO/date-like input into a Date. Returns `null` on empty /
 * unparsable input so callers can decide the placeholder ("—", "N/A", …).
 */
export function parseISO(input) {
  if (!input) return null;
  const d = input instanceof Date ? input : new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Break a Date into IST calendar parts using `Intl.DateTimeFormat`.
 * `Intl` correctly handles the DST-free +05:30 conversion in one call
 * without hand-rolled epoch math.
 */
function _partsIST(d) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: IST,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const out = {};
  for (const p of parts) out[p.type] = p.value;
  // `hour12: false` still emits "24" for midnight on some engines — normalise.
  if (out.hour === "24") out.hour = "00";
  return out;
}

/** `DD-MM-YYYY HH:MM` (24-hour) in IST. Empty input → "—". */
export function fmtDateTimeIST(input, fallback = "—") {
  const d = parseISO(input);
  if (!d) return fallback;
  const p = _partsIST(d);
  return `${p.day}-${p.month}-${p.year} ${p.hour}:${p.minute}`;
}

/** `DD-MM-YYYY HH:MM:SS` (24-hour) in IST — used on audit logs where seconds matter. */
export function fmtDateTimeSecIST(input, fallback = "—") {
  const d = parseISO(input);
  if (!d) return fallback;
  const p = _partsIST(d);
  return `${p.day}-${p.month}-${p.year} ${p.hour}:${p.minute}:${p.second}`;
}

/** `DD-MM-YYYY` in IST — used on order/invoice/GRN date columns. */
export function fmtDateIST(input, fallback = "—") {
  const d = parseISO(input);
  if (!d) return fallback;
  const p = _partsIST(d);
  return `${p.day}-${p.month}-${p.year}`;
}

/** `HH:MM` (24-hour) in IST — used on shift charts / live monitor. */
export function fmtTimeIST(input, fallback = "—") {
  const d = parseISO(input);
  if (!d) return fallback;
  const p = _partsIST(d);
  return `${p.hour}:${p.minute}`;
}

/**
 * Break a Date into IST 12-hour parts (`hour` 01-12, `dayPeriod` AM/PM).
 * Used by the 12-hour document-audit formatters below.
 */
function _parts12IST(d) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: IST,
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: true,
  }).formatToParts(d);
  const out = {};
  for (const p of parts) out[p.type] = p.value;
  return out;
}

/** `hh:mm:ss AM/PM` in IST — document creation-time on CWO View. Empty → "—". */
export function fmtTime12IST(input, fallback = "—") {
  const d = parseISO(input);
  if (!d) return fallback;
  const p = _parts12IST(d);
  return `${p.hour}:${p.minute}:${p.second} ${(p.dayPeriod || "").toUpperCase()}`;
}

/** `DD-MM-YYYY hh:mm:ss AM/PM` in IST — document date+time on CWO View. Empty → "—". */
export function fmtDateTime12IST(input, fallback = "—") {
  const d = parseISO(input);
  if (!d) return fallback;
  const p = _partsIST(d);
  const t = _parts12IST(d);
  return `${p.day}-${p.month}-${p.year} ${t.hour}:${t.minute}:${t.second} ${(t.dayPeriod || "").toUpperCase()}`;
}

/**
 * Today's ISO date (`YYYY-MM-DD`) in IST — for report date-range defaults.
 * Matches the backend `today_ist()` helper.
 */
export function todayIST() {
  const p = _partsIST(new Date());
  return `${p.year}-${p.month}-${p.day}`;
}

/**
 * Now, as an IST-offset ISO string (`YYYY-MM-DDTHH:mm:ss+05:30`).
 * Handy for stamping optimistic UI state before the backend echo arrives.
 */
export function nowISTIso() {
  const p = _partsIST(new Date());
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:${p.second}+05:30`;
}

export default {
  parseISO,
  fmtDateTimeIST,
  fmtDateTimeSecIST,
  fmtDateIST,
  fmtTimeIST,
  fmtTime12IST,
  fmtDateTime12IST,
  todayIST,
  nowISTIso,
};
